import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import {
  Subject,
  Subscription,
  firstValueFrom,
  distinctUntilChanged,
  debounceTime,
} from 'rxjs';
import { ToastService } from '../../../services/toast/toast.service';
import { IEnvironment } from '../../../models/environment.model';
import { AESEncryptionUtil } from '../../../../utils/services/encryption.util';
import { ValidationErrors, AbstractControl } from '@angular/forms';
import { formatFileSizeBytesUtil } from '../../../../utils/utils/base-64-uri-to-file-converter.util';
import { IIremboFileUpload } from '../../../models/irembo-file-upload-response.model';
import { KeycloakService } from 'keycloak-angular';
import { IPreviousAttachmentEvent } from '../../../models/previous-attachment-event';
import { ICertificateAttachmentEvent } from '../../../models/certificate-attachment-event';
import { UserDocumentType } from '../../../models/user-document-type.enum';
import { FileUploadSource } from '../../../models/file-upload-source.enum';
import { getCertificateNameByLocale } from '../../../../utils/utils/application-form.util';
import { VirusScanStatus } from '../../../../models/virus-scan.model';
import {
  findFormControl,
  findFormControlInScope,
} from '../../../../utils/utils/data-fetch-widget-utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormStateService } from '../../../services/formly/form-state.service';
import { TranslateService } from '@ngx-translate/core';

const FILEMANAGER_URL = '/application/v1/file-manager/application/attachment';
const CERTIFICATE_URL = '/document-generation/v1/file-manager/certificate';

interface IExternalFileUpload {
  fileName: string;
  fileSize: number;
  fileType: string;
  fileDownloadName: string;
  content?: string; // Optional since content is removed after external upload
  source: FileUploadSource;
  uploadResponse?: any;
}

interface RequestTransformation {
  url: string;
  method?: string;
  headers?: Record<string, string>;
  bodyParameters?: any;
  formParameters?: Record<string, any>;
}

interface PayloadMappingConfig {
  key: string;
  sourceField?: string;
  staticValue?: any;
  isStatic: boolean;
}

@Component({
  standalone: false,
  selector: 'irembogov-custom-external-file-upload',
  templateUrl: './custom-external-file-upload.component.html',
})
export class CustomExternalFileUploadComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit, OnDestroy
{
  private subscriptions = new Subscription();
  keepAliveInterval?: any;

  noFileActionLabel = 'Click to upload';
  noFileSubLabelHtml = 'or drag and drop';

  private _uploadStatus:
    | 'idle'
    | 'uploading'
    | 'completed'
    | 'error'
    | 'deleting' = 'idle';

  get uploadStatus() {
    return this._uploadStatus;
  }

  set uploadStatus(
    value: 'idle' | 'uploading' | 'completed' | 'error' | 'deleting'
  ) {
    this._uploadStatus = value;
  }
  progress = new Subject<number>();
  errorMessage?: string;
  uploadedFile?: IExternalFileUpload;
  currentProgressValue = 0;
  deleteInProgress = false;

  private environment: IEnvironment;

  // Properties matching custom-file-upload exactly
  maxSizeText = 'max.';
  totalPreviousAttachments = 0;
  totalCertificateAttachments = 0;
  previousAttachments: IPreviousAttachmentEvent[] = [];
  certificateAttachments: ICertificateAttachmentEvent[] = [];
  filePath: string | undefined;
  loadingFile = false;
  allowUploadPreviousFiles = true;

  page = 0;
  size = 10;
  sort = 'dateCreated,asc';
  placeholder = 'Select file to upload';
  dataField = 'data.content';

  file: IIremboFileUpload | null | undefined;
  photoValidationResponse: Record<string, unknown> | undefined;
  validatingPhoto = false;
  duplicateFile = false;

  // Properties from base component for validation checking
  hasErrors = false;
  invalidPhoto = false;
  fileAttachmentNotValid = false;
  scanningForVirus = false;
  virusScanStatus: VirusScanStatus = VirusScanStatus.NOT_SCANNED;
  base64Content = '';

  constructor(
    private cd: ChangeDetectorRef,
    private http: HttpClient,
    private toastService: ToastService,
    private keycloakService: KeycloakService,
    private modalService: NgbModal,
    @Inject('environment') environment: IEnvironment,
    private readonly formStateService: FormStateService,
    private readonly translateService: TranslateService
  ) {
    super();
    this.environment = environment;
    this.allowUploadPreviousFiles =
      this.environment.allowUploadPreviousFiles ?? true;
  }

  ngOnInit() {
    this.updateNoFileSubLabelHtml();
    this.setupFileRemovalListener();
    this.setupValidationListener();
    this.setupValueRestorationListener();
    this.updateFormValidation();
  }

  private setupFileRemovalListener() {
    // Listen for form control value changes to detect file removal
    this.subscriptions.add(
      this.field.formControl?.valueChanges.subscribe(value => {
        // If value changes from having a file to null, it was removed
        if (value === null && this.file) {
          this.onFileRemoved();
        }
      })
    );
  }

  private setupValidationListener() {
    // Update validation whenever upload status changes (but not too frequently)
    this.subscriptions.add(
      this.progress
        .pipe(
          distinctUntilChanged(),
          debounceTime(100) // Limit validation updates during rapid progress changes
        )
        .subscribe(() => {
          // Only update validation if not in active upload/delete state
          // to prevent interfering with the upload process
          if (
            this.uploadStatus !== 'uploading' &&
            this.uploadStatus !== 'deleting'
          ) {
            this.updateFormValidation();
          }
        })
    );
  }

  private setupValueRestorationListener() {
    this.subscriptions.add(
      this.field.formControl?.valueChanges
        .pipe(
          distinctUntilChanged(
            (a, b) => JSON.stringify(a) === JSON.stringify(b)
          )
        )
        .subscribe(value => {
          // Detect state restoration (value populated from model)
          // Only restore if:
          // 1. Value exists and is an object
          // 2. Value has upload indicators (uploadResponse or fileDownloadName)
          // 3. Component doesn't already have file set (avoid redundant restoration)
          if (
            value &&
            typeof value === 'object' &&
            (value.uploadResponse || value.fileDownloadName) &&
            !this.file
          ) {
            // Restore completed state
            this.uploadStatus = 'completed';
            this.uploadedFile = value;

            // CRITICAL: Set the file property so UI shows uploaded state
            this.file = {
              fileName: value.fileName,
              fileSize: value.fileSize,
              fileType: value.fileType,
              fileDownloadName: value.fileDownloadName,
              content: value.content || '',
            } as IIremboFileUpload;

            // Update form validation to reflect completed state
            this.updateFormValidation();
            this.cd.detectChanges();
          }
        })
    );
  }

  private updateFormValidation() {
    if (!this.field.formControl) {
      return;
    }

    const isRequired = this.field.props?.['required'] === true;
    const currentErrors = this.field.formControl.errors || {};

    if (this.uploadStatus === 'uploading' || this.uploadStatus === 'deleting') {
      // During upload/delete operations, field is always invalid (required or optional)
      const newErrors = {
        ...currentErrors,
        uploadInProgress: {
          message: this.translateService.instant(
            'Upload in progress, please wait...'
          ),
        },
      };
      this.field.formControl.setErrors(newErrors);
    } else if (this.uploadStatus === 'completed' && this.uploadedFile) {
      // Upload completed successfully, remove upload-specific errors
      const otherErrors = { ...currentErrors };
      delete otherErrors['uploadInProgress'];
      delete otherErrors['uploadError'];
      const hasOtherErrors = Object.keys(otherErrors).length > 0;
      const newErrors = hasOtherErrors ? otherErrors : null;
      this.field.formControl.setErrors(newErrors);
    } else if (this.uploadStatus === 'error') {
      // Upload failed, field remains invalid
      const newErrors = {
        ...currentErrors,
        uploadError: {
          message:
            this.errorMessage || this.translateService.instant('Upload failed'),
        },
      };
      this.field.formControl.setErrors(newErrors);
    } else if (
      this.uploadStatus === 'idle' &&
      !this.uploadedFile &&
      isRequired
    ) {
      // No file uploaded yet, field is invalid only if originally required
      const newErrors = {
        ...currentErrors,
        required: {
          message: this.translateService.instant('This field is required'),
        },
      };
      this.field.formControl.setErrors(newErrors);
    } else if (
      this.uploadStatus === 'idle' &&
      !this.uploadedFile &&
      !isRequired
    ) {
      // Optional field with no upload - remove upload-related errors but keep others
      const otherErrors = { ...currentErrors };
      delete otherErrors['uploadInProgress'];
      delete otherErrors['uploadError'];
      delete otherErrors['required'];
      const hasOtherErrors = Object.keys(otherErrors).length > 0;
      const newErrors = hasOtherErrors ? otherErrors : null;
      this.field.formControl.setErrors(newErrors);
    }

    this.field.formControl.markAsTouched();
  }

  private updateNoFileSubLabelHtml(): void {
    this.noFileSubLabelHtml = 'or drag and drop <br />';

    const validation = this.field.props?.['validation'];
    if (!validation) return;

    let fileFormats = '';
    if (
      validation['allowedFormats'] &&
      Array.isArray(validation['allowedFormats'])
    ) {
      fileFormats = validation['allowedFormats'].join(', ').toUpperCase();
    }

    const sizeLimits: string[] = [];

    this.calculateSizeLimit(
      validation['maximumUploadSize'],
      sizeLimits,
      'max. '
    );

    this.calculateSizeLimit(
      validation['minimumUploadSize'],
      sizeLimits,
      'min. '
    );

    this.noFileSubLabelHtml = `or drag and drop <br />${fileFormats}${
      sizeLimits.length > 0 ? '(' + sizeLimits.join(',') + ')' : ''
    }`;
    this.cd.detectChanges();
  }

  private calculateSizeLimit(
    size: number | undefined,
    sizeLimits: string[],
    prepend?: string
  ): void {
    if (size && !Number.isNaN(+size)) {
      sizeLimits.push(prepend + formatFileSizeBytesUtil(+size * 1024, 0));
    }
  }

  onFileUploadEvent(file: IIremboFileUpload) {
    // Handle standard attachment logic first (like custom-file-upload)
    const isImage = file['fileType'].includes('image');
    this.photoValidationResponse = {};
    this.file = file;

    if (this.field.props['validatePhoto'] && file['content'] && isImage) {
      const fileContent = file.content as string;
      const base64Str = fileContent.replace(/^data:image\/[a-z]+;base64,/, '');
      this.validatingPhoto = true;
      this.validatePhoto(base64Str).subscribe({
        next: res => {
          const checks = res['checks'] as Record<string, string>;
          this.photoValidationResponse = { ...checks, has_checks: true };
          this.validatingPhoto = false;
          this.cd.detectChanges();
        },
        error: () => {
          this.photoValidationResponse = { has_checks: false };
          this.validatingPhoto = false;
          this.cd.detectChanges();
        },
      });
    }

    const externalFile: IExternalFileUpload = {
      fileName: file.fileName,
      fileSize: file.fileSize,
      fileType: file.fileType,
      fileDownloadName: file.fileDownloadName,
      content: typeof file.content === 'string' ? file.content : '',
      source: FileUploadSource.DEVICE,
      uploadResponse: undefined,
    };

    // The base component validation hasn't run yet when this event fires
    // We need to wait for the next tick to allow base component validation to complete
    setTimeout(() => {
      this.checkValidationAndProceed(externalFile);
    }, 0);

    this.cd.detectChanges();
  }

  onFileRemoved() {
    const deleteEndpointCode = this.field.props?.['deleteEndpointCode'];

    if (
      deleteEndpointCode &&
      this.uploadedFile &&
      this.uploadStatus === 'completed'
    ) {
      this.deleteUploadedFile();
    } else {
      this.resetComponentState();
    }
  }

  private resetComponentState() {
    this.uploadStatus = 'idle';
    this.uploadedFile = undefined;
    this.errorMessage = undefined;
    this.currentProgressValue = 0;
    this.progress.next(0);
  }

  resetToIdle() {
    this.uploadStatus = 'idle';
    this.errorMessage = undefined;
    this.currentProgressValue = 0;
    this.progress.next(0);
  }

  /**
   * Check validation status and proceed with external upload if valid
   */
  private checkValidationAndProceed(externalFile: IExternalFileUpload) {
    this.errorMessage = undefined;

    const hasValidationErrors = this.hasFileValidationErrors();

    if (!hasValidationErrors) {
      // File passed all validations, proceed with external upload
      this.uploadedFile = externalFile;
      this.startExternalUpload(externalFile);
    } else {
      // File validation failed, don't proceed with external upload
      this.uploadedFile = undefined;
      this.uploadStatus = 'error';
      this.updateFormValidation();
    }

    this.cd.detectChanges();
  }

  /**
   * Check if the base file upload component has validation errors
   * Uses the form control validation for reliable validation checking
   */
  private hasFileValidationErrors(): boolean {
    if (!this.file) {
      return true; // No file means validation failed
    }

    // Use form control validation as the most reliable method
    if (this.field.formControl) {
      // Check current errors without triggering validation
      const errors = this.field.formControl.errors;

      // Check for validation errors that should prevent external upload
      if (errors) {
        const blockingErrors = [
          'invalidfileformat',
          'minimumUploadSize',
          'maximumUploadSize',
          'duplicateAttachment',
          'invalidPhoto',
          'virusDetected',
          'virusScanError',
          'notUniqueDataInAttachment',
        ];

        return blockingErrors.some(errorKey => errors[errorKey]);
      }
    }

    // Fallback to checking individual properties if form control approach fails
    return (
      this.hasErrors ||
      this.invalidPhoto ||
      this.duplicateFile ||
      this.fileAttachmentNotValid ||
      this.validatingPhoto ||
      this.scanningForVirus ||
      this.virusScanStatus === VirusScanStatus.INFECTED ||
      this.virusScanStatus === VirusScanStatus.ERROR
    );
  }

  private startExternalUpload(file: IExternalFileUpload) {
    this.uploadStatus = 'uploading';
    this.errorMessage = undefined;
    this.currentProgressValue = 0;

    // Set form as invalid during upload to prevent submission
    this.updateFormValidation();

    // Show initial progress
    this.progress.next(0);
    this.cd.detectChanges();

    // Start session keep-alive if enabled
    this.startSessionKeepAlive();

    // Simulate initial preparation phase
    this.progress.next(5);

    // Get upload configuration from integration
    this.getUploadConfiguration(file)
      .then(config => {
        // Configuration received
        this.progress.next(15);
        this.performExternalUpload(config, file);
      })
      .catch(error => {
        this.setError('Failed to get upload configuration: ' + error.message);
        this.stopSessionKeepAlive();
      });
  }

  private async getUploadConfiguration(
    file: IExternalFileUpload
  ): Promise<RequestTransformation> {
    const endpointCode = this.field.props?.['endpointCode'];
    if (!endpointCode) {
      throw new Error(
        'endpointCode is required for external file upload component'
      );
    }

    const payload = this.buildPayload(file);

    const url = `${this.environment.apiGatewayBaseUrl}/integration/v1/endpoints/${endpointCode}/evaluate`;

    return firstValueFrom(this.http.post<any>(url, payload)).then(
      async response => {
        // The evaluate endpoint returns the encrypted data directly in response.data
        const encryptedData = response.data;
        return await this.decryptTransformation(encryptedData);
      }
    );
  }

  private buildPayload(file?: IExternalFileUpload): Record<string, any> {
    const payloadMapping: PayloadMappingConfig[] =
      this.field.props?.['payloadMapping'] || [];
    const payload: Record<string, any> = {};

    payloadMapping.forEach(mapping => {
      if (mapping.isStatic) {
        payload[mapping.key] = mapping.staticValue;
      } else if (mapping.sourceField) {
        const formControl = this.findControl(mapping.sourceField);
        const value = formControl?.value;
        payload[mapping.key] = value;
      }
    });

    // Add file metadata if provided
    if (file) {
      payload['fileMetadata'] = {
        fileName: file.fileName,
        fileSize: file.fileSize,
        fileType: file.fileType,
        fileDownloadName: file.fileDownloadName || file.fileName,
      };
    }

    return payload;
  }

  private async decryptTransformation(
    encryptedTransformation: string
  ): Promise<RequestTransformation> {
    try {
      const decryptionKey = this.environment.AESEncryptionKey;
      const decryptedString = await this.decryptAES256GCM(
        encryptedTransformation,
        decryptionKey
      );
      return JSON.parse(decryptedString);
    } catch (error) {
      console.error('GCM decryption failed, trying fallback:', error);
      const decryptionKey = this.environment.AESEncryptionKey;
      const decryptedString = AESEncryptionUtil.decryptPayload(
        encryptedTransformation,
        decryptionKey
      );
      return JSON.parse(decryptedString);
    }
  }

  private performExternalUpload(
    transformation: RequestTransformation,
    file: IExternalFileUpload
  ) {
    const baseUrl = this.buildUrlFromTransformation(transformation);

    // Prepare the request body/form data
    const requestBody = this.buildRequestBody(transformation, file);

    // Get headers (including auth headers processed by backend)
    // Remove Content-Type header if present - let browser set it automatically with boundary for FormData
    const headers = transformation.headers || {};
    if (
      headers['Content-Type'] &&
      headers['Content-Type'].includes('multipart/form-data')
    ) {
      delete headers['Content-Type'];
    }
    if (
      headers['content-type'] &&
      headers['content-type'].includes('multipart/form-data')
    ) {
      delete headers['content-type'];
    }

    // Get HTTP method
    const method = transformation.method || 'POST';

    this.subscriptions.add(
      this.http
        .request(method, baseUrl, {
          body: requestBody,
          headers,
          reportProgress: true,
          observe: 'events',
        })
        .subscribe({
          next: (event: HttpEvent<any>) => {
            if (event.type === HttpEventType.UploadProgress) {
              if (event.total) {
                // Calculate progress with base offset (15% for preparation)
                const uploadProgress = Math.round(
                  (85 * event.loaded) / event.total
                );
                const totalProgress = 15 + uploadProgress;
                this.currentProgressValue = Math.min(totalProgress, 100);
                this.progress.next(this.currentProgressValue);

                // Ensure form stays invalid during progress updates
                if (
                  this.uploadStatus === 'uploading' &&
                  this.field.formControl &&
                  this.field.formControl.valid
                ) {
                  const newErrors = {
                    ...this.field.formControl.errors,
                    uploadInProgress: {
                      message: this.translateService.instant(
                        'Upload in progress, please wait...'
                      ),
                    },
                  };
                  this.field.formControl.setErrors(newErrors);
                }

                this.cd.detectChanges();
              }
            } else if (event.type === HttpEventType.Response) {
              // Upload complete, show processing
              this.progress.next(100);
              this.cd.detectChanges();
              this.handleUploadSuccess(event.body);
            }
          },
          error: error => {
            this.handleUploadError(error);
          },
        })
    );
  }

  private buildUrlFromTransformation(
    transformation: RequestTransformation
  ): string {
    // The URL from the backend should be complete and ready to use
    const url = transformation.url;

    if (!url) {
      throw new Error('No URL provided in transformation response');
    }

    return url;
  }

  private buildRequestBody(
    transformation: RequestTransformation,
    file: IExternalFileUpload
  ): FormData {
    // Always use FormData for file uploads, combining formParameters and bodyParameters
    const formData = new FormData();

    // Get the file field name from configuration, default to 'file'
    const fileFieldName = this.field.props?.['fileFieldName'] || 'file';

    // Add formParameters to FormData
    if (transformation.formParameters) {
      this.addParametersToFormData(
        formData,
        transformation.formParameters,
        file
      );
    }

    // Add bodyParameters to FormData (convert body parameters to form fields)
    if (transformation.bodyParameters) {
      this.addParametersToFormData(
        formData,
        transformation.bodyParameters,
        file
      );
    }

    // Always append the file if content exists
    if (file.content) {
      const blob = this.base64ToBlob(file.content, file.fileType);
      formData.append(fileFieldName, blob, file.fileName);
    }

    return formData;
  }

  private addParametersToFormData(
    formData: FormData,
    parameters: any,
    file: IExternalFileUpload
  ): void {
    if (!parameters || typeof parameters !== 'object') {
      return;
    }

    // Handle nested objects by flattening them
    this.flattenAndAddToFormData(formData, parameters, '', file);
  }

  private flattenAndAddToFormData(
    formData: FormData,
    obj: any,
    prefix: string,
    file: IExternalFileUpload
  ): void {
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = obj[key];
        const fieldName = prefix ? `${prefix}.${key}` : key;

        if (value !== null && value !== undefined) {
          if (typeof value === 'object' && !Array.isArray(value)) {
            // Recursively handle nested objects
            this.flattenAndAddToFormData(formData, value, fieldName, file);
          } else if (Array.isArray(value)) {
            // Handle arrays by adding each element with indexed names
            value.forEach((item, index) => {
              const indexedFieldName = `${fieldName}[${index}]`;
              if (typeof item === 'object') {
                this.flattenAndAddToFormData(
                  formData,
                  item,
                  indexedFieldName,
                  file
                );
              } else {
                formData.append(indexedFieldName, String(item));
              }
            });
          } else if (
            typeof value === 'string' &&
            this.isFilePlaceholder(value)
          ) {
            // Handle file placeholders
            this.appendFileValueToFormData(formData, fieldName, value, file);
          } else {
            // Handle primitive values
            formData.append(fieldName, String(value));
          }
        }
      }
    }
  }

  private buildFormData(
    formConfig: Record<string, any>,
    file: IExternalFileUpload
  ): FormData {
    const formData = new FormData();

    // Add form fields from config
    for (const key of Object.keys(formConfig)) {
      const value = formConfig[key];

      if (key === 'file' && typeof value === 'object') {
        // Handle nested file object structure
        this.appendFileToFormData(formData, key, value, file);
      } else if (typeof value === 'string' && this.isFilePlaceholder(value)) {
        // Handle direct file placeholders
        this.appendFileValueToFormData(formData, key, value, file);
      } else {
        // Regular form field
        formData.append(key, value);
      }
    }

    // Always append the actual file if not already handled
    if (!Object.keys(formConfig).includes('file') && file.content) {
      // Convert file content from base64 to Blob
      const blob = this.base64ToBlob(file.content, file.fileType);
      formData.append('file', blob, file.fileName);
    }

    return formData;
  }

  private appendFileToFormData(
    formData: FormData,
    key: string,
    fileConfig: any,
    file: IExternalFileUpload
  ) {
    for (const fileKey of Object.keys(fileConfig)) {
      const fileValue = fileConfig[fileKey];
      this.appendFileValueToFormData(
        formData,
        `${key}.${fileKey}`,
        fileValue,
        file
      );
    }
  }

  private appendFileValueToFormData(
    formData: FormData,
    fieldName: string,
    placeholder: string,
    file: IExternalFileUpload
  ) {
    switch (placeholder) {
      case '{{FILE_CONTENT}}': {
        // Convert base64 to Blob and append (only if content exists)
        if (file.content) {
          const blob = this.base64ToBlob(file.content, file.fileType);
          formData.append(fieldName, blob, file.fileName);
        }
        break;
      }
      case '{{FILE_NAME}}':
        formData.append(fieldName, file.fileName);
        break;
      case '{{FILE_TYPE}}':
        formData.append(fieldName, file.fileType);
        break;
      case '{{FILE_SIZE}}':
        formData.append(fieldName, file.fileSize.toString());
        break;
      case '{{FILE_DOWNLOAD_NAME}}':
        formData.append(fieldName, file.fileDownloadName || file.fileName);
        break;
      default:
        // Regular value
        formData.append(fieldName, placeholder);
    }
  }

  private buildJsonBody(bodyConfig: any, file: IExternalFileUpload): string {
    // Replace file placeholders in JSON body
    const processedBody = this.replacePlaceholders(bodyConfig, file);
    return JSON.stringify(processedBody);
  }

  private replacePlaceholders(obj: any, file: IExternalFileUpload): any {
    if (typeof obj === 'string') {
      return this.replaceStringPlaceholder(obj, file);
    } else if (Array.isArray(obj)) {
      return obj.map(item => this.replacePlaceholders(item, file));
    } else if (obj && typeof obj === 'object') {
      const result: any = {};
      for (const key in obj) {
        result[key] = this.replacePlaceholders(obj[key], file);
      }
      return result;
    }
    return obj;
  }

  private replaceStringPlaceholder(
    value: string,
    file: IExternalFileUpload
  ): any {
    switch (value) {
      case '{{FILE_CONTENT}}':
        return file.content;
      case '{{FILE_NAME}}':
        return file.fileName;
      case '{{FILE_TYPE}}':
        return file.fileType;
      case '{{FILE_SIZE}}':
        return file.fileSize;
      case '{{FILE_DOWNLOAD_NAME}}':
        return file.fileDownloadName || file.fileName;
      default:
        return value;
    }
  }

  private isFilePlaceholder(value: string): boolean {
    return [
      '{{FILE_CONTENT}}',
      '{{FILE_NAME}}',
      '{{FILE_TYPE}}',
      '{{FILE_SIZE}}',
      '{{FILE_DOWNLOAD_NAME}}',
    ].includes(value);
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    // Remove data URL prefix if present
    const base64Data = base64.includes(',') ? base64.split(',')[1] : base64;

    // Convert base64 to binary
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }

  private handleUploadSuccess(response: any) {
    // Ensure progress shows 100% briefly before completing
    this.progress.next(100);

    setTimeout(() => {
      this.uploadStatus = 'completed';
      this.stopSessionKeepAlive();

      if (this.uploadedFile) {
        // Create a new file object without content for external uploads
        // The file is now stored externally, so we only keep metadata and API response
        const fileWithoutContent = { ...this.uploadedFile };
        delete fileWithoutContent.content;
        this.uploadedFile = {
          ...fileWithoutContent,
          uploadResponse: response,
        };

        // CRITICAL FIX: Set this.file so UI shows the uploaded state
        this.file = {
          fileName: this.uploadedFile.fileName,
          fileSize: this.uploadedFile.fileSize,
          fileType: this.uploadedFile.fileType,
          fileDownloadName: this.uploadedFile.fileDownloadName,
          content: this.uploadedFile.content || '',
        } as IIremboFileUpload;
      }

      // Populate form fields based on response
      this.populateFormFields(response);

      // Update form validation first, before setValue
      this.updateFormValidation();

      // Set the field value after validation is set (to avoid clearing errors)
      // Only store metadata and API response, not the file content
      this.field.formControl?.setValue(this.uploadedFile, { emitEvent: false });

      this.toastService.show({
        body: this.translateService.instant('File uploaded successfully'),
        type: 'success',
      });

      //this.cd.detectChanges();
    }, 500); // Brief delay to show 100% completion
  }

  private handleUploadError(error: any) {
    this.uploadStatus = 'error';
    this.stopSessionKeepAlive();

    const errorMessage =
      error.error?.message ||
      error.message ||
      this.translateService.instant('Upload failed');
    this.setError(errorMessage);

    // Update form validation after error
    this.updateFormValidation();

    this.toastService.show({
      body: errorMessage,
      type: 'error',
    });
  }

  async deleteUploadedFile() {
    if (!this.uploadedFile || this.deleteInProgress) return;

    // Check if delete is configured
    const deleteEndpointCode = this.field.props?.['deleteEndpointCode'];
    if (!deleteEndpointCode) {
      this.toastService.show({
        body: this.translateService.instant(
          'Delete functionality not configured'
        ),
        type: 'warning',
      });
      return;
    }

    try {
      this.deleteInProgress = true;
      this.uploadStatus = 'deleting';

      // Set form as invalid during delete to prevent submission
      this.updateFormValidation();
      this.cd.detectChanges();

      // Get delete configuration from integration
      const deleteConfig = await this.getDeleteConfiguration();

      // Perform the actual delete operation
      await this.performExternalDelete(deleteConfig);

      // Reset the component state after successful deletion
      this.resetAfterDelete();

      this.toastService.show({
        body: this.translateService.instant('File deleted successfully'),
        type: 'success',
      });
    } catch (error) {
      this.handleDeleteError(error);
    } finally {
      if (this.file && !this.duplicateFile) {
        // Compute SHA-256 hash of file content
        const fileHash = await this.formStateService.computeSHA256(
          this.base64Content
        );
        this.formStateService.removeHashAttachmentFromArray(
          this.file?.fileName,
          fileHash
        );
      }
      this.deleteInProgress = false;
      this.cd.detectChanges();
    }
  }

  private async getDeleteConfiguration(): Promise<RequestTransformation> {
    const deleteEndpointCode = this.field.props?.['deleteEndpointCode'];
    if (!deleteEndpointCode) {
      throw new Error('deleteEndpointCode is required for file deletion');
    }

    const payload = this.buildDeletePayload();

    const url = `${this.environment.apiGatewayBaseUrl}/integration/v1/endpoints/${deleteEndpointCode}/evaluate`;

    return firstValueFrom(this.http.post<any>(url, payload)).then(
      async response => {
        const encryptedData = response.data;
        return await this.decryptTransformation(encryptedData);
      }
    );
  }

  private buildDeletePayload(): Record<string, any> {
    const deletePayloadMapping: PayloadMappingConfig[] =
      this.field.props?.['deletePayloadMapping'] || [];
    const payload: Record<string, any> = {};

    deletePayloadMapping.forEach(mapping => {
      if (mapping.isStatic) {
        payload[mapping.key] = mapping.staticValue;
      } else if (mapping.sourceField) {
        const formControl = this.findControl(mapping.sourceField);
        const value = formControl?.value;
        payload[mapping.key] = value;
      }
    });

    // Add uploaded file metadata for deletion
    if (this.uploadedFile) {
      payload['fileMetadata'] = {
        fileName: this.uploadedFile.fileName,
        fileSize: this.uploadedFile.fileSize,
        fileType: this.uploadedFile.fileType,
      };

      // Include upload response data that might contain file ID or reference
      if (this.uploadedFile.uploadResponse) {
        payload['uploadResponse'] = this.uploadedFile.uploadResponse;
      }
    }

    return payload;
  }

  private async performExternalDelete(transformation: RequestTransformation) {
    const baseUrl = this.buildUrlFromTransformation(transformation);
    const requestBody = this.buildDeleteRequestBody(transformation);

    // Remove Content-Type header if present - let browser set it automatically with boundary for FormData
    const headers = transformation.headers || {};
    if (
      headers['Content-Type'] &&
      headers['Content-Type'].includes('multipart/form-data')
    ) {
      delete headers['Content-Type'];
    }
    if (
      headers['content-type'] &&
      headers['content-type'].includes('multipart/form-data')
    ) {
      delete headers['content-type'];
    }

    const method = transformation.method || 'DELETE';

    return firstValueFrom(
      this.http.request(method, baseUrl, {
        body: requestBody,
        headers,
        observe: 'response',
      })
    );
  }

  private buildDeleteRequestBody(
    transformation: RequestTransformation
  ): FormData {
    // Use FormData for delete requests, combining formParameters and bodyParameters
    const formData = new FormData();

    // Add formParameters to FormData
    if (transformation.formParameters) {
      this.addDeleteParametersToFormData(
        formData,
        transformation.formParameters
      );
    }

    // Add bodyParameters to FormData
    if (transformation.bodyParameters) {
      this.addDeleteParametersToFormData(
        formData,
        transformation.bodyParameters
      );
    }

    // If no parameters provided, add default file metadata
    if (!transformation.formParameters && !transformation.bodyParameters) {
      if (this.uploadedFile?.uploadResponse) {
        formData.append(
          'fileReference',
          JSON.stringify(this.uploadedFile.uploadResponse)
        );
      }
      if (this.uploadedFile?.fileName) {
        formData.append('fileName', this.uploadedFile.fileName);
      }
    }

    return formData;
  }

  private addDeleteParametersToFormData(
    formData: FormData,
    parameters: any
  ): void {
    if (!parameters || typeof parameters !== 'object') {
      return;
    }

    // Handle nested objects by flattening them (similar to upload parameters)
    this.flattenAndAddDeleteToFormData(formData, parameters, '');
  }

  private flattenAndAddDeleteToFormData(
    formData: FormData,
    obj: any,
    prefix: string
  ): void {
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = obj[key];
        const fieldName = prefix ? `${prefix}.${key}` : key;

        if (value !== null && value !== undefined) {
          if (typeof value === 'object' && !Array.isArray(value)) {
            // Recursively handle nested objects
            this.flattenAndAddDeleteToFormData(formData, value, fieldName);
          } else if (Array.isArray(value)) {
            // Handle arrays by adding each element with indexed names
            value.forEach((item, index) => {
              const indexedFieldName = `${fieldName}[${index}]`;
              if (typeof item === 'object') {
                this.flattenAndAddDeleteToFormData(
                  formData,
                  item,
                  indexedFieldName
                );
              } else {
                formData.append(indexedFieldName, String(item));
              }
            });
          } else if (
            typeof value === 'string' &&
            this.isDeletePlaceholder(value)
          ) {
            // Handle delete placeholders
            this.appendDeleteValueToFormData(formData, fieldName, value);
          } else {
            // Handle primitive values
            formData.append(fieldName, String(value));
          }
        }
      }
    }
  }

  private buildDeleteFormData(formConfig: Record<string, any>): FormData {
    const formData = new FormData();

    for (const key of Object.keys(formConfig)) {
      const value = formConfig[key];

      if (typeof value === 'string' && this.isDeletePlaceholder(value)) {
        this.appendDeleteValueToFormData(formData, key, value);
      } else {
        formData.append(key, value);
      }
    }

    return formData;
  }

  private buildDeleteJsonBody(bodyConfig: any): string {
    const processedBody = this.replaceDeletePlaceholders(bodyConfig);
    return JSON.stringify(processedBody);
  }

  private appendDeleteValueToFormData(
    formData: FormData,
    fieldName: string,
    placeholder: string
  ) {
    if (!this.uploadedFile) return;

    switch (placeholder) {
      case '{{FILE_NAME}}':
        formData.append(fieldName, this.uploadedFile.fileName);
        break;
      case '{{FILE_TYPE}}':
        formData.append(fieldName, this.uploadedFile.fileType);
        break;
      case '{{FILE_SIZE}}':
        formData.append(fieldName, this.uploadedFile.fileSize.toString());
        break;
      case '{{UPLOAD_RESPONSE}}':
        formData.append(
          fieldName,
          JSON.stringify(this.uploadedFile.uploadResponse)
        );
        break;
      default:
        formData.append(fieldName, placeholder);
    }
  }

  private replaceDeletePlaceholders(obj: any): any {
    if (typeof obj === 'string') {
      return this.replaceDeleteStringPlaceholder(obj);
    } else if (Array.isArray(obj)) {
      return obj.map(item => this.replaceDeletePlaceholders(item));
    } else if (obj && typeof obj === 'object') {
      const result: any = {};
      for (const key in obj) {
        result[key] = this.replaceDeletePlaceholders(obj[key]);
      }
      return result;
    }
    return obj;
  }

  private replaceDeleteStringPlaceholder(value: string): any {
    if (!this.uploadedFile) return value;

    switch (value) {
      case '{{FILE_NAME}}':
        return this.uploadedFile.fileName;
      case '{{FILE_TYPE}}':
        return this.uploadedFile.fileType;
      case '{{FILE_SIZE}}':
        return this.uploadedFile.fileSize;
      case '{{UPLOAD_RESPONSE}}':
        return this.uploadedFile.uploadResponse;
      default:
        return value;
    }
  }

  private isDeletePlaceholder(value: string): boolean {
    return [
      '{{FILE_NAME}}',
      '{{FILE_TYPE}}',
      '{{FILE_SIZE}}',
      '{{UPLOAD_RESPONSE}}',
    ].includes(value);
  }

  private resetAfterDelete() {
    // Reset component state
    this.uploadStatus = 'idle';
    this.uploadedFile = undefined;
    this.errorMessage = undefined;
    this.currentProgressValue = 0;
    this.progress.next(0);

    // Update form validation first, before setValue
    this.updateFormValidation();

    // Reset form control value after validation is set (to avoid clearing errors)
    this.field.formControl?.setValue(null, { emitEvent: false });

    // Reset populated fields
    this.resetPopulatedFields();

    // Reset file upload component
    this.file = null;
    this.photoValidationResponse = {};
    this.validatingPhoto = false;
  }

  private resetPopulatedFields() {
    const populates = this.field.props?.['populates'] || [];

    populates.forEach((populate: any) => {
      const formControl = this.findControl(populate.targetKey);
      if (formControl) {
        formControl.setValue(null);
        formControl.markAsPristine();
        formControl.markAsUntouched();
      }
    });

    this.cd.detectChanges();
  }

  private handleDeleteError(error: any) {
    this.uploadStatus = 'completed'; // Go back to completed state
    const errorMessage =
      error.error?.message || error.message || 'Delete failed';

    // Update form validation after delete error
    this.updateFormValidation();

    this.toastService.show({
      body: errorMessage,
      type: 'error',
    });
  }

  private populateFormFields(response: any) {
    const populates = this.field.props?.['populates'] || [];

    populates.forEach((populate: any) => {
      const formControl = this.findControl(populate.targetKey);
      if (formControl) {
        let value = this.getNestedValue(response, populate.valueKey);

        if (Array.isArray(populate.valueKey) && populate.separator) {
          value = populate.valueKey
            .map((key: string) => this.getNestedValue(response, key))
            .filter((v: any) => v !== undefined)
            .join(populate.separator);
        }

        formControl.setValue(value);
      }
    });
  }

  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  private setError(message: string) {
    this.uploadStatus = 'error';
    this.errorMessage = message;
    this.field.formControl?.setErrors({ uploadError: { message } });
    this.cd.detectChanges();
  }

  private startSessionKeepAlive() {
    const sessionConfig = this.field.props?.['sessionKeepAlive'];
    if (sessionConfig?.enableKeepAlive) {
      const interval = sessionConfig.keepAliveInterval || 60000;

      this.keepAliveInterval = setInterval(() => {
        this.refreshAuthToken();
      }, interval);
    }
  }

  private stopSessionKeepAlive() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = undefined;
    }
  }

  private refreshAuthToken() {
    // Silent token refresh - completely invisible to user
    this.keycloakService
      .updateToken(60) // Refresh if expires within 60 seconds
      .then(() => {
        // Silent success - no logging, no user feedback
      })
      .catch(() => {
        // Try forced refresh silently
        this.keycloakService
          .updateToken(-1) // Force refresh
          .then(() => {
            // Silent success
          })
          .catch(() => {
            // Silent failure - let upload continue
          });
      });
  }

  handleFileValidationErrors(errors: ValidationErrors) {
    if (errors) {
      const errorKeys = Object.keys(errors);
      const firstError = errors[errorKeys[0]];
      this.setError(
        firstError?.message ||
          this.translateService.instant('File validation failed')
      );
    }
  }

  private async decryptAES256GCM(
    encryptedText: string,
    encryptionKey: string
  ): Promise<string> {
    try {
      // Check if Web Crypto API is available
      if (!window.crypto || !window.crypto.subtle) {
        throw new Error('Web Crypto API not available');
      }

      // Convert base64 to buffer
      const decode = new Uint8Array(
        atob(encryptedText)
          .split('')
          .map(c => c.charCodeAt(0))
      );

      // Extract IV (first 12 bytes)
      const iv = decode.subarray(0, 12);
      // Extract salt (next 16 bytes)
      const salt = decode.subarray(12, 28);
      // Remaining is ciphertext (includes auth tag)
      const cipherText = decode.subarray(28);

      // For GCM mode, extract auth tag (last 16 bytes) and ciphertext
      const authTagLength = 16; // 128 bits = 16 bytes
      const authTag = cipherText.subarray(-authTagLength);
      const encrypted = cipherText.subarray(0, -authTagLength);

      // Derive key using PBKDF2 with Web Crypto API
      const keyMaterial = await window.crypto.subtle.importKey(
        'raw',
        new TextEncoder().encode(encryptionKey),
        'PBKDF2',
        false,
        ['deriveKey']
      );

      const derivedKey = await window.crypto.subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt: salt,
          iterations: 65536,
          hash: 'SHA-256',
        },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        false,
        ['decrypt']
      );

      // Combine encrypted data with auth tag for GCM
      const dataWithTag = new Uint8Array(encrypted.length + authTag.length);
      dataWithTag.set(encrypted);
      dataWithTag.set(authTag, encrypted.length);

      // Decrypt
      const decryptedBuffer = await window.crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: iv,
          tagLength: 128,
        },
        derivedKey,
        dataWithTag
      );

      // Convert to string
      return new TextDecoder().decode(decryptedBuffer);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      throw new Error('GCM decryption failed: ' + errorMessage);
    }
  }

  // Additional event handlers for full compatibility
  onSelectFile() {
    // Load user documents if not already loaded
    if (this.previousAttachments.length === 0) {
      const { dataset } = this.field.props;
      if (dataset?.['params']) {
        const externalParams = dataset['params'] as Record<string, unknown>;
        if (
          externalParams['size'] &&
          typeof externalParams['size'] === 'number'
        ) {
          this.size = externalParams['size'];
        }
        if (
          externalParams['sort'] &&
          typeof externalParams['sort'] === 'string'
        ) {
          this.sort = externalParams['sort'];
        }
      }

      this.loadUserDocuments(
        '/application/v1/application/find-user-certificates',
        UserDocumentType.CERTIFICATES
      );
      this.loadUserDocuments(
        '/application/v1/application/find-user-previous-attachments',
        UserDocumentType.PREVIOUS_ATTACHMENTS
      );
    }
  }

  selectPreviousAttachmentEvent(event: IPreviousAttachmentEvent) {
    // Close the modal first, just like in custom-file-upload
    this.modalService.dismissAll();

    // Handle previous attachment selection - upload to external service
    this.handleAttachmentSelection(event, 'previous');
  }

  selectCertificateAttachmentEvent(event: ICertificateAttachmentEvent) {
    // Close the modal first, just like in custom-file-upload
    this.modalService.dismissAll();

    // Handle certificate attachment selection - upload to external service
    this.handleAttachmentSelection(event, 'certificate');
  }

  private handleAttachmentSelection(
    event: IPreviousAttachmentEvent | ICertificateAttachmentEvent,
    type: 'previous' | 'certificate'
  ) {
    // First, we need to load the actual file content from the server
    // This mimics what the regular file upload component does
    this.loadingFile = true;
    this.cd.detectChanges();

    const fileName =
      type === 'previous'
        ? (event as IPreviousAttachmentEvent).fileName
        : getCertificateNameByLocale(
            (event as ICertificateAttachmentEvent).certificateNames
          );

    const fileDownloadName =
      type === 'previous'
        ? (event as IPreviousAttachmentEvent).fileOriginalName
        : getCertificateNameByLocale(
            (event as ICertificateAttachmentEvent).certificateNames
          );

    if (type === 'previous') {
      // Handle previous attachment - use the same approach as custom-file-upload
      const fileManagerUrl = `${this.environment.apiGatewayBaseUrl}${FILEMANAGER_URL}`;
      this.subscriptions.add(
        this.getBlob(
          {
            id: (event as IPreviousAttachmentEvent).applicationId,
            fileName: (event as IPreviousAttachmentEvent).fileName,
          },
          fileManagerUrl
        ).subscribe({
          next: (httpEvent: HttpEvent<Blob>) => {
            if (httpEvent.type === HttpEventType.Response && httpEvent.body) {
              this.convertBlobToBase64(
                httpEvent.body,
                fileName,
                fileDownloadName
              );
            }
          },
          error: () => {
            this.loadingFile = false;
            this.toastService.show({
              body: this.translateService.instant(
                'Failed to load selected file'
              ),
              type: 'error',
            });
            this.cd.detectChanges();
          },
        })
      );
    } else {
      // Handle certificate attachment
      const certificateUrl = `${this.environment.apiGatewayBaseUrl}${CERTIFICATE_URL}`;
      this.subscriptions.add(
        this.getBlob(
          {
            certificateName: getCertificateNameByLocale(
              (event as ICertificateAttachmentEvent).certificateNames
            ),
          },
          certificateUrl
        ).subscribe({
          next: (httpEvent: HttpEvent<Blob>) => {
            if (httpEvent.type === HttpEventType.Response && httpEvent.body) {
              this.convertBlobToBase64(
                httpEvent.body,
                fileName,
                fileDownloadName
              );
            }
          },
          error: () => {
            this.loadingFile = false;
            this.toastService.show({
              body: this.translateService.instant(
                'Failed to load selected file'
              ),
              type: 'error',
            });
            this.cd.detectChanges();
          },
        })
      );
    }
  }

  private convertBlobToBase64(
    blob: Blob,
    fileName: string,
    fileDownloadName: string
  ) {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        this.loadingFile = false;

        this.base64Content = reader.result as string;
        const fileSize = blob.size;
        const fileType = blob.type;

        // Create a fresh file object
        const newFile: IIremboFileUpload = {
          fileName,
          fileSize,
          fileType,
          fileDownloadName,
          content: this.base64Content,
          source: FileUploadSource.PREVIOUS_ATTACHMENT,
        };

        // Assign it so Angular detects a new reference
        this.file = { ...newFile };

        // Update form control immediately to show the file in the UI
        this.field.formControl.setValue(this.file);
        this.cd.detectChanges();

        // Compute SHA-256 hash and check duplicates
        const fileHash = await this.formStateService.computeSHA256(
          this.base64Content
        );

        if (this.formStateService.isDuplicateFile(fileHash)) {
          this.duplicateFile = true;
          this.field.formControl.setErrors({
            ...(this.field.formControl.errors || {}),
            duplicateAttachment: true,
          });

          this.cd.markForCheck();
          return;
        }
        this.formStateService.saveHashAttachmentInArray(fileName, fileHash);

        const externalFile: IExternalFileUpload = {
          fileName: newFile.fileName,
          fileSize: newFile.fileSize,
          fileType: newFile.fileType,
          fileDownloadName: newFile.fileDownloadName,
          content: newFile.content as string,
          source: newFile.source,
          uploadResponse: undefined,
        };

        // Store it for later use
        this.uploadedFile = externalFile;

        // Notify UI
        this.cd.detectChanges();

        // Begin upload
        this.startExternalUpload(externalFile);
      } catch (err) {
        console.error('Error converting blob to base64:', err);
        this.toastService.show({
          body: this.translateService.instant(
            'Failed to process selected file'
          ),
          type: 'error',
        });
        this.cd.detectChanges();
      }
    };

    reader.onerror = () => {
      this.loadingFile = false;
      this.toastService.show({
        body: this.translateService.instant('Failed to process selected file'),
        type: 'error',
      });
      this.cd.detectChanges();
    };

    reader.readAsDataURL(blob);
  }

  handlePreviousAttachmentPagination(event: Record<string, number>) {
    this.handlePagination(event, UserDocumentType.PREVIOUS_ATTACHMENTS);
  }

  handleCertificateAttachmentPagination(event: Record<string, number>) {
    this.handlePagination(event, UserDocumentType.CERTIFICATES);
  }

  private handlePagination(
    event: Record<string, number>,
    type: UserDocumentType
  ) {
    const limit = event['scrollHeight'] - event['clientHeight'];
    const moreItems =
      type === UserDocumentType.CERTIFICATES
        ? this.totalCertificateAttachments !==
          this.certificateAttachments.length
        : this.totalPreviousAttachments !== this.previousAttachments.length;

    if (event['scrollPosition'] === limit && moreItems) {
      this.page++;
      if (type === UserDocumentType.PREVIOUS_ATTACHMENTS) {
        this.loadUserDocuments(
          '/application/v1/application/find-user-previous-attachments',
          UserDocumentType.PREVIOUS_ATTACHMENTS
        );
      }
      if (type === UserDocumentType.CERTIFICATES) {
        this.loadUserDocuments(
          '/application/v1/application/find-user-certificates',
          UserDocumentType.CERTIFICATES
        );
      }
    }
  }

  private loadUserDocuments(endpoint: string, type: UserDocumentType) {
    const url = `${this.environment.apiGatewayBaseUrl}${endpoint}`;
    const { dataset } = this.field.props;
    const params: Record<string, unknown> = {
      page: this.page,
      size: this.size,
      sort: this.sort,
    };

    const keyString = dataset?.['dataField']
      ? dataset['dataField']
      : this.dataField;
    if (keyString) {
      const queryParams = Object.keys(params)
        .map(key => `${key}=${params[key]}`)
        .join('&');

      this.subscriptions.add(
        this.http
          .get<Record<string, unknown>>(`${url}?${queryParams}`)
          .subscribe({
            next: (res: Record<string, unknown>) => {
              const data = res['data'] as Record<string, unknown>;
              const totalElements = data['totalElements'] as number;
              this.loadCertificates(type, res, keyString, totalElements);
              this.loadPreviousDocuments(type, res, keyString, totalElements);
              this.cd.detectChanges();
            },
            error: () => {
              this.toastService.show({
                body: `Error loading ${type}`,
                type: 'error',
              });
              this.cd.detectChanges();
            },
          })
      );
    }
  }

  private loadCertificates(
    type: UserDocumentType,
    res: Record<string, unknown>,
    keyString: string,
    totalElements: number
  ) {
    if (type === UserDocumentType.CERTIFICATES) {
      this.totalCertificateAttachments = totalElements;
      const certificateAttachmentsData = Array.isArray(res)
        ? (res as ICertificateAttachmentEvent[])
        : this.getValueFromNestedObject<ICertificateAttachmentEvent>(
            res,
            keyString
          );
      this.certificateAttachments.push(...certificateAttachmentsData);
      // Construct the display name for the certificate attachments
      const locale = localStorage.getItem('locale') ?? 'en-US';
      this.certificateAttachments.forEach(attachment => {
        const serviceName = attachment.serviceName;
        const applicationNumber = attachment.certificateNames[locale]
          .split('-')
          .pop();
        attachment.displayName = `${serviceName} - ${applicationNumber}`;
      });
    }
  }

  private loadPreviousDocuments(
    type: UserDocumentType,
    res: Record<string, unknown>,
    keyString: string,
    totalElements: number
  ) {
    if (type === UserDocumentType.PREVIOUS_ATTACHMENTS) {
      this.totalPreviousAttachments = totalElements;
      const previousAttachmentsData = Array.isArray(res)
        ? (res as IPreviousAttachmentEvent[])
        : this.getValueFromNestedObject<IPreviousAttachmentEvent>(
            res,
            keyString
          );
      this.previousAttachments.push(...previousAttachmentsData);
      // Construct the display name for the previous attachments
      this.previousAttachments.forEach(attachment => {
        const fileName = attachment.fileName;
        const applicationNumber = fileName.split('_')[0];
        const originalName = attachment.fileOriginalName;
        attachment.displayName = `${applicationNumber} - ${originalName}`;
      });
    }
  }

  private getValueFromNestedObject<T>(
    obj: Record<string, unknown>,
    keyString: string
  ): T[] {
    const keys = keyString.split('.');
    let value = obj;
    for (const key of keys) {
      value = value[key] as Record<string, unknown>;
      if (value === undefined) {
        throw new Error(`Invalid key string: ${keyString}`);
      }
    }
    return value as unknown as T[];
  }

  private validatePhoto(data: string) {
    const url = `${this.environment.apiGatewayBaseUrl}/photo-validation/api/photo/validate`;
    return this.http.post<Record<string, unknown>>(url, { data });
  }

  /**
   * Find form control using the same logic as generic-data-fetch component
   * This allows accessing controls from any level of the form hierarchy
   */
  private findControl(targetKey: string): AbstractControl | null {
    // Check if we should search globally or within scope
    const searchGlobally =
      this.field.props?.['populateRelativeToRootForm'] !== false;

    return searchGlobally
      ? findFormControl(this.form, targetKey)
      : findFormControlInScope(this.form, targetKey);
  }

  /**
   * Get blob from file manager - same implementation as custom-file-upload
   */
  private getBlob(
    queryParams: Record<string, unknown>,
    fileManagerUrl: string
  ) {
    const params = Object.keys(queryParams)
      .map(key => `${key}=${encodeURIComponent(queryParams[key] as string)}`)
      .join('&');
    return this.http.get(`${fileManagerUrl}?${params}`, {
      responseType: 'blob',
      reportProgress: true,
      observe: 'events',
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.stopSessionKeepAlive();
  }
}
